import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trash2, ShoppingBag, Loader2, ArrowRight } from "lucide-react";
import { useApp, formatEUR } from "@/context/AppContext";
import { getProduct } from "@/data/products";
import { CartLineControls, hasMissingDates } from "@/components/CartLineControls";
import { startCheckout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function CartPage() {
  const { t, lang, cart, removeFromCart } = useApp();
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("canceled")) {
      const id = setTimeout(() => toast.info(t.cart.canceled), 100);
      return () => clearTimeout(id);
    }
  }, []); // eslint-disable-line

  const lines = cart.map((i) => ({ ...i, product: getProduct(i.id) })).filter((l) => l.product);
  const ttc = lines.reduce((s, l) => s + l.product.price * l.quantity, 0);
  const ht = ttc / 1.2;
  const tva = ttc - ht;
  const missing = hasMissingDates(lines);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      await startCheckout(cart);
    } catch {
      toast.error(lang === "fr" ? "Erreur lors du paiement." : "Payment error.");
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-16 sm:px-8" data-testid="cart-page">
      <h1 className="text-4xl font-extrabold tracking-tighter text-cea-ink sm:text-5xl">{t.cart.title}</h1>

      {lines.length === 0 ? (
        <div className="mt-12 flex flex-col items-center gap-4 rounded-3xl border border-slate-200 bg-cea-surface p-16 text-center text-slate-500">
          <ShoppingBag className="h-12 w-12 text-slate-300" />
          <p>{t.cart.empty}</p>
          <Button asChild className="rounded-full bg-cea-accent text-white hover:bg-cea-navy">
            <Link to="/catalogue" data-testid="cart-page-catalogue">{t.cta.viewCatalogue}</Link>
          </Button>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {lines.map((l) => (
              <div key={l.id} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`cart-page-line-${l.id}`}>
                <img src={l.product.images[0]} alt={l.product.name[lang]} className="h-24 w-28 shrink-0 rounded-xl object-cover" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-start justify-between">
                    <h3 className="font-bold text-cea-ink">{l.product.name[lang]}</h3>
                    <button onClick={() => removeFromCart(l.id)} className="text-slate-400 hover:text-red-500" data-testid={`cart-page-remove-${l.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <p className="text-sm text-slate-500">{formatEUR(l.product.price)}</p>
                  <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-2">
                    <div className="w-full sm:w-auto sm:min-w-[220px] sm:max-w-xs sm:flex-1"><CartLineControls line={l} prefix="cart-page" /></div>
                    <p className="font-bold text-cea-navy">{formatEUR(l.product.price * l.quantity)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="h-fit rounded-2xl border border-slate-200 bg-white p-6 lg:sticky lg:top-24">
            <div className="space-y-3">
              <div className="flex justify-between text-sm text-slate-600"><span>{t.cart.ht}</span><span data-testid="cart-page-ht">{formatEUR(ht)}</span></div>
              <div className="flex justify-between text-sm text-slate-600"><span>{t.cart.tva}</span><span data-testid="cart-page-tva">{formatEUR(tva)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-3 text-lg font-bold text-cea-ink"><span>{t.cart.ttc}</span><span data-testid="cart-page-ttc">{formatEUR(ttc)}</span></div>
            </div>
            {missing && <p className="mt-4 text-xs font-medium text-amber-600" data-testid="cart-page-missing-dates">{t.dates.missing}</p>}
            <Button onClick={handleCheckout} disabled={loading || missing} data-testid="cart-page-checkout" className="mt-5 w-full rounded-full bg-cea-accent py-6 text-base font-semibold text-white hover:bg-cea-navy">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t.cta.checkout}<ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
